import { createContext, useContext, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import type { ReflectionSource } from '../../hooks/usePracticeReflection';

/**
 * CORE-1751 — открыть окно рефлексии откуда угодно: после выхода из
 * Live-сессии и кнопкой в сообщении Агапеллы.
 *
 * У практики из библиотеки окно живёт на её экране. Live-сессия так не может:
 * человек из неё уже ушёл, экрана комнаты больше нет, а сообщение Агапеллы
 * открывают в чате, в шторке или в виджете. Поэтому окно поднято к корню
 * приложения (`ReflectionLauncherProvider`), а сюда вынесена только ручка.
 *
 * Модуль намеренно не тянет AuthContext и сами окна: его импортируют хуки
 * комнаты, а AuthContext тащит за собой Sentry, и наборы тестов этих хуков
 * перестали бы собираться.
 */
export interface ReflectionRequest {
  /** Настоящий id завершения — к нему привяжется запись дневника. */
  completionId: number;
  source: ReflectionSource;
}

export interface ReflectionLauncher {
  openReflection: (request: ReflectionRequest) => void;
  /**
   * Придержать поздравление, пока решается, будет ли окно (возврат QA 23.10).
   * Между уходом из Live-сессии и окном — запрос к серверу, и поздравление за
   * ту же сессию, уже лежащее в очереди, успевало выйти вперёд окна.
   */
  hold?: (pending: Promise<unknown>) => void;
}

/** Без провайдера (тесты, экраны вне корня) открывать нечего — и ничего не падает. */
const NOOP: ReflectionLauncher = { openReflection: () => {} };

export const ReflectionLauncherContext = createContext<ReflectionLauncher>(NOOP);

export function useReflectionLauncher(): ReflectionLauncher {
  return useContext(ReflectionLauncherContext);
}

/**
 * CORE-1751 (решение продукта, Мария, 19.10) — «сначала рефлексия,
 * поздравление — после того как человек её закроет».
 *
 * Поздравление по Пути Света приходит на начисление света души, а свет души
 * начисляется за ту же засчитанную практику, после которой открывается окно
 * рефлексии. Раньше оба появлялись разом, и виджет Агапеллы ложился поверх
 * окна. Теперь поздравление ждёт, пока на экране есть окно рефлексии.
 *
 * Окон рефлексии в приложении два вида: у корня (выход из Live-сессии, кнопка
 * в сообщении Агапеллы) и на экране практики. Состояние «окно на экране» одно,
 * у корня, в `ReflectionLauncherProvider`; каждое окно сообщает о себе сюда
 * само — это делает `usePracticeReflection`, общий для всех. Второго флага,
 * который пришлось бы держать в согласии с первым, нет.
 *
 * Возврат QA 23.10: в тот же набор встаёт и окно, которое вот-вот откроется, —
 * сводка завершённой Live-сессии, с которой человек ещё не ушёл, и запрос
 * предложения после ухода. Иначе поздравление за эту сессию, начисленное к её
 * концу, выходило раньше окна.
 */
export interface ReflectionVisibility {
  /** Хоть одно окно рефлексии сейчас на экране — приватность, вопросы или «Сохранено». */
  visible: boolean;
  /** Окно `id` показано или убрано. */
  report: (id: symbol, visible: boolean) => void;
}

const NO_VISIBILITY: ReflectionVisibility = { visible: false, report: () => {} };

export const ReflectionVisibilityContext = createContext<ReflectionVisibility>(NO_VISIBILITY);

/** Видно ли сейчас окно рефлексии — для того, кто должен его подождать. */
export function useReflectionVisible(): boolean {
  return useContext(ReflectionVisibilityContext).visible;
}

/**
 * Набор окон, которые сейчас на экране. Набор, а не флаг: окно у корня и окно
 * на экране практики могут смениться одно другим, и флаг, сброшенный ушедшим,
 * спрятал бы того, кто остался.
 */
export function nextVisibleWindows(
  current: ReadonlySet<symbol>, id: symbol, visible: boolean,
): ReadonlySet<symbol> {
  if (visible === current.has(id)) return current;
  const next = new Set(current);
  if (visible) next.add(id);
  else next.delete(id);
  return next;
}

/**
 * Придержать поздравление, пока `pending` не завершится — как угодно: с окном,
 * без него или ошибкой. Ручка `hold` у корня — ровно это.
 */
export function holdWhilePending(
  report: ReflectionVisibility['report'], pending: Promise<unknown>,
): void {
  const id = Symbol('reflection-offer');
  report(id, true);
  const release = () => report(id, false);
  pending.then(release, release);
}

/** Окно сообщает, видно ли оно; размонтированное — убрано. */
export function useReportReflectionVisible(visible: boolean): void {
  const { report } = useContext(ReflectionVisibilityContext);
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol('reflection-window');
  useEffect(() => {
    report(idRef.current as symbol, visible);
  }, [visible, report]);
  useEffect(() => () => report(idRef.current as symbol, false), [report]);
}

/**
 * Человек вышел из Live-сессии: спросить сервер и, если участие засчитано,
 * открыть окно.
 *
 * Спрашиваем после того, как ушёл запрос выхода (`afterLeave`): сервер считает
 * участие по строкам присутствия, и уход должен быть в них записан. Ответ
 * сервера уже отметил предложение сделанным — второго окна и сообщения при
 * возвращении по этой сессии не будет. Любой сбой — тишина: рефлексия
 * предлагается сверху, и выход из комнаты не должен из-за неё ломаться.
 *
 * Пока ответа нет, поздравление по Пути Света придержано (`hold`): решение
 * продукта — сначала рефлексия, потом поздравление, а свет души за эту сессию
 * к выходу уже начислен.
 */
export function offerLiveReflectionAfterExit(
  roomId: number | undefined | null,
  launcher: ReflectionLauncher,
  afterLeave?: Promise<unknown>,
): Promise<void> {
  if (!roomId) return Promise.resolve();
  const pending = (async () => {
    try {
      await afterLeave;
    } catch {
      // выход не дошёл — спросить всё равно можно
    }
    try {
      const offer = await api.claimRoomReflectionOffer(roomId);
      if (offer) launcher.openReflection({ completionId: offer.completionId, source: 'live_exit' });
    } catch {
      // намеренно молча
    }
  })();
  launcher.hold?.(pending);
  return pending;
}
